import { ApiResourceState, ReportType } from '../types';
import { StyledFormWrapper } from './style';
import { Box, Stack, useTheme } from '@mui/material';
import { useAuthentication } from 'src/features/authentication/context/authentication.context';
import { useFormContext, useWatch } from 'react-hook-form';
import { useEffect, useState } from 'react';
import { allTimezones, useTimezoneSelect } from 'react-timezone-select';
import { ReportFormBtnControl } from '../components/report-form-btns/ReportFormBtnControl';
import { isAdminOrSiteManager } from 'src/utilities/permissions';
import { ReportsService } from 'src/services/reports/reports.service';
import { parseReportTemplatePayload } from '../utils/parseReportTemplatePayload';
import { useTemplateState } from '../context/templateContext';
import useQueryParamsActions from 'src/hooks/useQueryParamsActions';
import { useSnackbar } from 'notistack';
import { useTemplateCache } from '../context/templateCacheContext';
import { PDFReportFormSkelton } from '../skeltons/PDFReportFormSkelton';
import { SiteStatusReportForm } from '../components/pdf-report-template-forms/SiteStatusReportForm';
import { StatusReportForm } from '../components/pdf-report-template-forms/StatusReportForm';
import { useResponsive } from 'src/hooks/useResponsive';
import { usePDFReportData } from 'src/hooks/usePDFReportData';

type PDFReportFormProps = {
  title: string;
  onUndo: (sites: any) => void;
  disableActions: boolean;
  onSubmit: () => void;
  reportType: string;
};
export const PDFReportForm = ({
  title,
  onUndo,
  disableActions,
  onSubmit,
  reportType,
}: PDFReportFormProps) => {
  const {
    getCurrentRole,
    customerId: { value: customerId },
    siteId: { value: siteId },
  } = useAuthentication();

  const theme = useTheme();
  const isMobile = useResponsive('down', 'sm');
  const isMd = useResponsive('down', 'md');

  const isTablet = useResponsive('down', 'lg');
  const isExtraLarge = useResponsive('down', 'xl');
  const methods = useFormContext();
  const labelStyle = 'original';

  const { options } = useTimezoneSelect({ labelStyle, timezones: allTimezones });

  const { trigger } = methods;
  const { checkTemplateForChanges } = useTemplateState();
  const { get: getQueryParam, append } = useQueryParamsActions();

  const { getValues, watch, setValue } = useFormContext();
  const data = getValues();
  const currentSite = getValues('data.siteId.value');
  const [reportState, setReportState] = useState<{
    status: ApiResourceState;
    error: Error | null;
    data: any;
  }>({
    status: 'INITIAL',
    error: null,
    data: null,
  });

  const currentRole = getCurrentRole(customerId, siteId);
  const disabledPermisionSaveBtn = isAdminOrSiteManager(currentRole);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [customSelected, setIsCustomSelected] = useState<boolean>(false);
  const [filteredAssets, setFilteredAssets] = useState<
    { label: string; value: string; siteId: string }[]
  >([]);

  useEffect(() => {
    setIsCustomSelected(typeof watch('data.timeFrame') !== 'string');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watch('data.timeFrame')]);

  const templateId = getQueryParam('templateId');

  const { filteredCustomerSites, isPDFDataLoading, assets, customerSites } = usePDFReportData({
    customerId,
    reportType,
  });
  const { enqueueSnackbar } = useSnackbar();
  const templateCacheState = useTemplateCache();

  useEffect(() => {
    if (reportType === ReportType.PDF_SITE_STATUS_REPORT) return;
    const selectedSite = filteredCustomerSites?.find((site) => site.value === currentSite);

    if (selectedSite?.location?.timeZone) {
      const matchingTimezone = options.find(
        (option) => option.value === selectedSite.location.timeZone
      );

      if (matchingTimezone) {
        setValue('data.timeZone', matchingTimezone, {
          shouldValidate: true,
          shouldDirty: true,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useWatch({ name: 'data.siteId' }), filteredCustomerSites]);

  useEffect(() => {
    const site = getValues('data.siteId');
    if (site && filteredCustomerSites?.length) {
      const match = filteredCustomerSites.find((s) => s.value === site.value);

      if (match && site !== match) {
        setValue('data.siteId', match, {
          shouldValidate: true,
          shouldDirty: false,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredCustomerSites]);

  useEffect(() => {
    setIsCustomSelected(typeof watch('data.timeFrame') !== 'string');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useWatch({ name: 'data.timeFrame' })]);

  const assetIdsLength = data?.data?.assetIds?.length;

  const onSaveHandler = async () => {
    const isValid = await trigger();
    const reportType = data?.header?.reportType?.id;
    if (!reportType) {
      return enqueueSnackbar('No report Type in header cannot parse payload');
    }

    if (!isValid) {
      return enqueueSnackbar('Please fill out all required fields', { variant: 'error' });
    }
    if (!data.data.timeZone) {
      return enqueueSnackbar('Timezone is required!', { variant: 'error' });
    }

    if (assetIdsLength === 0 && data.header.reportTemplate.value !== 'SITE_STATUS_REPORT') {
      return enqueueSnackbar('Asset is required', { variant: 'error' });
    }
    if (data?.data?.siteId?.length === 0) {
      return enqueueSnackbar('Site is required', { variant: 'error' });
    }

    setReportState((prevState) => ({ ...prevState, status: 'LOADING' }));
    try {
      const report = templateId
        ? await ReportsService.updateReportTemplate(
            templateId,
            customerId!,
            parseReportTemplatePayload(reportType, {
              ...data,
              title,
            })
          )
        : await ReportsService.saveReportTemplate(
            customerId!,
            parseReportTemplatePayload(reportType, { ...data, title })
          );
      templateCacheState[1]({});
      append('templateId', report.id);
      setReportState((prevState) => ({ ...prevState, data: report, status: 'SUCCESS' }));
      enqueueSnackbar('Report template successfully ' + (templateId ? 'updated' : 'saved') + '!', {
        variant: 'success',
      });
    } catch (err) {
      enqueueSnackbar(
        'Could not ' + (templateId ? 'update' : 'save') + ' report template!\n' + err.message,
        { variant: 'error' }
      );
      setReportState((prevState) => ({ ...prevState, error: err, status: 'ERROR' }));
    }
  };
  useEffect(() => {
    if (assets.loaded && assets.data?.length > 0) {
      const currentAssetIds = getValues('data.assetIds');

      if (currentAssetIds?.length > 0 && typeof currentAssetIds[0] === 'object') {
        const matched = currentAssetIds
          .map((selected: any) => assets.data.find((asset) => asset.value === selected.value))
          .filter(Boolean);

        if (matched.length > 0) {
          setValue('data.assetIds', matched);
        }
      }
    }
    setIsLoading(false);
  }, [assets.loaded, assets.data, getValues, setValue]);

  useEffect(() => {
    if (!assets.loaded) return;
    const filteredAssets = assets.data.filter((asset) => asset?.siteId === currentSite);
    setFilteredAssets(filteredAssets);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assets, watch('data.siteId')]);

  const gridLayout = () => {
    if (isMobile) return 'repeat(1, 1fr)';

    if (customSelected) {
      if (isMd || isExtraLarge) return 'repeat(2, 1fr)';
      if (isTablet) return 'repeat(1, 1fr)';
    }

    if (isTablet) return 'repeat(2, 1fr)';

    return 'repeat(6, 1fr)';
  };

  const stale = checkTemplateForChanges(methods.watch());
  const tagListLen = (watch('data.tagList') || []).length;

  const layout = gridLayout();
  const isSavedDisabled =
    reportState.status === 'LOADING' ||
    stale === 'INVALID' ||
    stale ||
    disableActions ||
    isPDFDataLoading ||
    disabledPermisionSaveBtn;
  const isPDFSiteStatusTagListEmpty =
    reportType === ReportType.PDF_SITE_STATUS_REPORT && tagListLen === 0 && isSavedDisabled;

  const isGenerateDisabled =
    reportState.status === 'LOADING' ||
    stale === 'INVALID' ||
    !stale ||
    disableActions ||
    isPDFSiteStatusTagListEmpty ||
    isLoading;

  if (isLoading || isPDFDataLoading) {
    return (
      <PDFReportFormSkelton
        isExtraLarge={isExtraLarge}
        theme={theme}
        isMobile={isMobile}
        isTablet={isTablet}
        gridLayout={layout}
      />
    );
  }

  const renderReportForm = () => {
    if (reportType === ReportType.PDF) {
      return (
        <StatusReportForm
          filteredCustomerSites={filteredCustomerSites}
          options={options}
          testId="status-report-form"
          currentSite={currentSite}
          layout={layout}
          filteredAssets={filteredAssets}
        />
      );
    } else if (reportType === ReportType.PDF_SITE_STATUS_REPORT) {
      return (
        <SiteStatusReportForm
          sites={customerSites.value}
          options={options}
          testId="site-status-report-form"
          assets={assets}
          layout={layout}
        />
      );
    }
  };

  return (
    <StyledFormWrapper
      theme={theme}
      isTablet={isTablet}
      isExtraLarge={isExtraLarge}
      isPdfReport={false}
    >
      <Box width="100%" display="flex" flexDirection="column">
        {renderReportForm()}
        <Stack direction="row" justifyContent="flex-end" spacing={2} mt={4}>
          <ReportFormBtnControl
            isGenerateDisabled={isGenerateDisabled}
            isSavedDisabled={isSavedDisabled}
            onSaveHandler={onSaveHandler}
            reportState={reportState}
            onUndo={onUndo}
            onSubmit={onSubmit}
            showTooltip={isGenerateDisabled && isPDFSiteStatusTagListEmpty}
            tooltipMessage={'At least one tag must be added'}
          />
        </Stack>
      </Box>
    </StyledFormWrapper>
  );
};
